import { typeDescriptions } from "../RSheets/RSheetsLogic";
import { COMMON_FIELDS } from "../RSheets/RSheetsFieldsModel";

export const comparisonOperatorLabels = {
  "=": "Is",
  "!=": "Is not",
  ">": "Greater than",
  "<": "Less than",
  ">=": "Greater than or equal to",
  "<=": "Less than or equal to"
};

export const rsTypeOptions = Object.entries(typeDescriptions).map(
	([id, label]) => ({
		id: Number(id),
		label,
	})
);

// ["=", "!=", ">", "<", ">=", "<="]
export const gci_exception_conditions = {
    rs_type: {
        operators: ["=", "!="],
        label: 'RS Type',
        options: rsTypeOptions,
        element_type: 'select'
    },

    // Tab: Property Information
    deal_generated_by: {
        operators: ["=", "!="],
        label: 'Deal Generated By',
        options: COMMON_FIELDS.deal_generated_by.options,
        element_type: 'radio',
        for_rs_type: [1,2,3,4,5,6,7,8,9]
    },
    // Tab: Property Information
    la_city_verification: {
        operators: ["=", "!="],
        label: 'Is Property Located in the City of L.A ?',
        options: COMMON_FIELDS.la_city_verification.options,
        element_type: 'radio'
    },
    // Tab: Property Information
    illi_represented_lessor: {
        operators: ["=", "!="],
        label: 'illi Represented - Lessor',
        options: COMMON_FIELDS.illi_represented_lessor.options,
        element_type: 'radio'
    },
    // Tab: Property Information
    illi_represented_lessee: {
        operators: ["=", "!="],
        label: 'illi Represented - Lessee',
        options: COMMON_FIELDS.illi_represented_lessee.options,
        element_type: 'radio'
    },
    // Tab: Property Information
    property_type: {
        operators: ["=", "!="],
        label: 'Use Type',
        options: COMMON_FIELDS.property_type.options,
        element_type: 'select'
    },
    // Tab: Property Information
    property: {
        operators: ["=", "!="],
        label: 'Property',
        element_type: 'property'
    },
    // Tab: Property Information
    illi_represented_lessor_agents: {
        operators: ["=", "!="],
        label: 'illi Represented - Lessor - Agent(s)',
        element_type: 'associate',
        people_type: 'agents'
    },
    // Tab: Property Information
    illi_represented_lessee_agents: {
        operators: ["=", "!="],
        label: 'illi Represented - Lessee - Agent(s)',
        element_type: 'associate',
        people_type: 'agents'
    },
    // Tab: Property Information
    listings_procured_by: {
        operators: ["=", "!="],
        label: 'Listings Procured By',
        element_type: 'contact'
    },

    // Tab: Premises Information
    loopnet_marketing_updated: {
        operators: ["=", "!="],
        label: 'Loopnet/Marketing Updated?',
        options: COMMON_FIELDS.loopnet_marketing_updated.options,
        element_type: 'radio'
    },
    // Tab: Premises Information
    window_signs_returned: {
        operators: ["=", "!="],
        label: 'Window Signs Returned?',
        options: COMMON_FIELDS.window_signs_returned.options,
        element_type: 'select'
    },

    // Tab: Lease Information
    type_of_lease: {
        operators: ["=", "!="],
        label: 'Type of Lease',
        options: ['New Lease', 'Renewal', 'Referral'],
        element_type: 'select'
    },
    // Tab: Lease Information
    lease_type: {
        operators: ["=", "!="],
        label: 'Lease Type',
        options: COMMON_FIELDS.lease_type.options,
        element_type: 'select'
    },
    // Tab: Lease Information
    flat_fee_commission: {
        operators: ["=", "!="],
        label: 'Flat Fee Commission',
        options: COMMON_FIELDS.flat_fee_commission.options,
        element_type: 'radio'
    },
    // Tab: Lease Information
    commission_based_off: {
        operators: ["=", "!="],
        label: 'Rent Based Off',
        options: COMMON_FIELDS.commission_based_off.options,
        element_type: 'radio'
    },
    // Tab: Lease Information
    rate_type: {
        operators: ["=", "!="],
        label: 'Rental Increases',
        options: COMMON_FIELDS.rate_type.options,
        element_type: 'radio'
    },
    // Tab: Lease Information
    early_possession: {
        operators: ["=", "!="],
        label: 'Early Possession',
        options: COMMON_FIELDS.early_possession.options,
        element_type: 'radio'
    },
    // Tab: Lease Information
    contingency: {
        operators: ["=", "!="],
        label: 'Contingency',
        options: COMMON_FIELDS.contingency.options,
        element_type: 'radio'
    },
    // Tab: Lease Information
    option_renewal: {
        operators: ["=", "!="],
        label: 'Option Renewal',
        options: COMMON_FIELDS.option_renewal.options,
        element_type: 'radio'
    },
    // Tab: Lease Information
    option_commission: {
        operators: ["=", "!="],
        label: 'Option Commission',
        options: COMMON_FIELDS.option_commission.options,
        element_type: 'radio'
    },

    // Tab: Lessee
    can_deal_be_submitted_to_public_relations: {
        operators: ["=", "!="],
        label: 'Can deal be submitted to Public Relations?',
        options: COMMON_FIELDS.can_deal_be_submitted_to_public_relations.options,
        element_type: 'radio'
    }
};

const getOptionValue = option => {
    if (!option || typeof option !== 'object') return option;
    if (Object.prototype.hasOwnProperty.call(option, 'id')) return option.id;
    if (Object.prototype.hasOwnProperty.call(option, 'value')) return option.value;
    return option.label;
};

const getOptionLabel = option => {
    if (!option || typeof option !== 'object') return option;
    return option.label || option.name || getOptionValue(option);
};

export const getConditionValueLabel = (field, value) => {
    if (value && typeof value === 'object') {
        return String(value.name || value.label || value.id || '');
    }

    const definition = gci_exception_conditions[field];
    if (!definition || !Array.isArray(definition.options)) return String(value);

    const matchingOption = definition.options.find(
        option => String(getOptionValue(option)) === String(value)
    );

    return matchingOption === undefined ? String(value) : String(getOptionLabel(matchingOption));
};

export const isConditionAvailable = (field, selectedConditions = []) => {
    const definition = gci_exception_conditions[field];
    if (!definition) return false;
    if (!Array.isArray(definition.for_rs_type)) return true;

    const selectedRsTypes = selectedConditions
        .filter(condition => condition.field === 'rs_type')
        .map(condition => String(condition.value));

    return definition.for_rs_type.some(
        rsType => selectedRsTypes.indexOf(String(rsType)) !== -1
    );
};
